import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Plus, CalendarDays, Loader2 } from 'lucide-react';
import { format, addMonths, subMonths } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { createQuittanceForPeriod, type QuittanceLease } from '@/lib/quittances';
import { SCI_MANAGERS } from '@/lib/managers';
import { INDEXATION_LEASE_COLUMNS } from '../../../supabase/functions/_shared/indexation.ts';

interface LeaseRow extends QuittanceLease {
  payment_day: number | null;
  properties: {
    id: string;
    name: string | null;
    address: string;
    scis: { id: string; name: string; manager: string | null } | null;
  } | null;
  tenants: {
    first_name: string | null;
    last_name: string | null;
    company_name: string | null;
    tenant_type: string | null;
  } | null;
}

function getMonthOptions() {
  const options: { value: string; label: string }[] = [];
  const now = new Date();
  for (let i = 24; i >= 1; i--) {
    const d = subMonths(now, i);
    options.push({ value: format(d, 'yyyy-MM'), label: format(d, 'MMMM yyyy', { locale: fr }) });
  }
  for (let i = 0; i <= 2; i++) {
    const d = addMonths(now, i);
    options.push({ value: format(d, 'yyyy-MM'), label: format(d, 'MMMM yyyy', { locale: fr }) });
  }
  return options;
}

function tenantName(t: LeaseRow['tenants']): string {
  if (!t) return '—';
  if ((t.tenant_type === 'professional' || t.tenant_type === 'company') && t.company_name) return t.company_name;
  return `${t.first_name || ''} ${t.last_name || ''}`.trim() || t.company_name || '—';
}

export function GenerateQuittancesDialog() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [selectedPeriods, setSelectedPeriods] = useState<string[]>([format(new Date(), 'yyyy-MM')]);
  const [monthPickerOpen, setMonthPickerOpen] = useState(false);
  const [filterManager, setFilterManager] = useState<string>('all');
  const [filterSci, setFilterSci] = useState<string>('all');
  const [selectedLeases, setSelectedLeases] = useState<Set<string>>(new Set());
  const [paymentDate, setPaymentDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));
  const [method, setMethod] = useState<string>('virement');

  const monthOptions = getMonthOptions();

  const { data: leases = [], isLoading } = useQuery({
    queryKey: ['leases-for-quittances'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('leases')
        .select(`
          id, rent_amount, charges_amount, vat_rate, rent_lines, payment_day, organization_id, ${INDEXATION_LEASE_COLUMNS},
          properties(id, name, address, scis(id, name, manager)),
          tenants(first_name, last_name, company_name, tenant_type)
        `)
        .eq('status', 'active');
      if (error) throw error;
      return (data ?? []) as unknown as LeaseRow[];
    },
    enabled: open,
  });

  const matchesManager = (l: LeaseRow) => filterManager === 'all' || l.properties?.scis?.manager === filterManager;

  const scis = Array.from(
    new Map(
      leases
        .filter(matchesManager)
        .map((l) => l.properties?.scis)
        .filter((s): s is { id: string; name: string; manager: string | null } => !!s)
        .map((s) => [s.id, s]),
    ).values(),
  );

  const visibleLeases = leases.filter(
    (l) => matchesManager(l) && (filterSci === 'all' || l.properties?.scis?.id === filterSci),
  );

  const changeManager = (value: string) => {
    setFilterManager(value);
    setFilterSci('all');
    setSelectedLeases(new Set());
  };

  const togglePeriod = (value: string) =>
    setSelectedPeriods((prev) => (prev.includes(value) ? prev.filter((p) => p !== value) : [...prev, value].sort()));
  const toggleLease = (id: string) =>
    setSelectedLeases((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const allVisibleSelected = visibleLeases.length > 0 && visibleLeases.every((l) => selectedLeases.has(l.id));
  const toggleSelectAll = () =>
    setSelectedLeases(allVisibleSelected ? new Set() : new Set(visibleLeases.map((l) => l.id)));

  const totalToCreate = selectedLeases.size * selectedPeriods.length;

  const generate = useMutation({
    mutationFn: async () => {
      const chosen = leases.filter((l) => selectedLeases.has(l.id));
      if (chosen.length === 0) throw new Error('Sélectionnez au moins un contrat');
      if (selectedPeriods.length === 0) throw new Error('Sélectionnez au moins une période');

      let created = 0;
      let skipped = 0;
      const errors: string[] = [];
      for (const lease of chosen) {
        for (const period of selectedPeriods) {
          try {
            const res = await createQuittanceForPeriod(lease, period, paymentDate, method);
            res.status === 'created' ? created++ : skipped++;
          } catch (e) {
            errors.push(`${tenantName(lease.tenants)} ${period}: ${e instanceof Error ? e.message : String(e)}`);
          }
        }
      }
      return { created, skipped, errors };
    },
    onSuccess: ({ created, skipped, errors }) => {
      queryClient.invalidateQueries({ queryKey: ['receipts-full'] });
      if (created > 0) toast.success(`${created} quittance(s) créée(s) en brouillon`);
      if (skipped > 0) toast.info(`${skipped} déjà existante(s), ignorée(s)`);
      if (errors.length > 0) toast.error(`${errors.length} erreur(s): ${errors[0]}`);
      if (created > 0 && errors.length === 0) {
        setSelectedLeases(new Set());
        setOpen(false);
      }
    },
    onError: (e: any) => toast.error(e.message),
  });

  const periodsLabel = selectedPeriods
    .map((p) => monthOptions.find((m) => m.value === p)?.label || p)
    .join(', ');

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Créer des quittances
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Créer des quittances</DialogTitle>
          <DialogDescription>
            Sélectionnez une ou plusieurs périodes et un ou plusieurs contrats. Les quittances sont créées en
            brouillon, puis validées avant envoi et dépôt sur la PDP.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Période(s)</Label>
            <Popover open={monthPickerOpen} onOpenChange={setMonthPickerOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start font-normal">
                  <CalendarDays className="h-4 w-4 mr-2" />
                  <span className="truncate">{periodsLabel || 'Choisir…'}</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64 p-0" align="start">
                <ScrollArea className="h-64">
                  <div className="p-2 space-y-1">
                    {monthOptions.map((m) => (
                      <label
                        key={m.value}
                        className={cn(
                          'flex items-center gap-2 rounded px-2 py-1.5 text-sm cursor-pointer hover:bg-accent',
                          selectedPeriods.includes(m.value) && 'bg-accent',
                        )}
                      >
                        <Checkbox
                          checked={selectedPeriods.includes(m.value)}
                          onCheckedChange={() => togglePeriod(m.value)}
                        />
                        <span className="capitalize">{m.label}</span>
                      </label>
                    ))}
                  </div>
                </ScrollArea>
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Date de paiement</Label>
            <Input type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} />
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Mode de paiement</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="virement">Virement</SelectItem>
                <SelectItem value="prelevement">Prélèvement</SelectItem>
                <SelectItem value="cheque">Chèque</SelectItem>
                <SelectItem value="especes">Espèces</SelectItem>
                <SelectItem value="carte">Carte</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Select value={filterManager} onValueChange={changeManager}>
              <SelectTrigger className="w-48"><SelectValue placeholder="Tous les gestionnaires" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les gestionnaires</SelectItem>
                {SCI_MANAGERS.map((m) => (
                  <SelectItem key={m} value={m}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterSci} onValueChange={setFilterSci}>
              <SelectTrigger className="w-56"><SelectValue placeholder="Toutes les SCI" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les SCI</SelectItem>
                {scis.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button variant="ghost" size="sm" onClick={toggleSelectAll} disabled={visibleLeases.length === 0}>
            {allVisibleSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
          </Button>
        </div>

        <div className="flex-1 min-h-0 rounded-lg border">
          <ScrollArea className="h-[280px]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10"></TableHead>
                  <TableHead>Locataire</TableHead>
                  <TableHead>Bien</TableHead>
                  <TableHead>SCI</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">Chargement…</TableCell></TableRow>
                ) : visibleLeases.length === 0 ? (
                  <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                    {filterManager !== 'all'
                      ? `Aucune SCI assignée à ${filterManager}. Assignez un gestionnaire depuis la page SCI.`
                      : 'Aucun contrat actif.'}
                  </TableCell></TableRow>
                ) : (
                  visibleLeases.map((l) => (
                    <TableRow key={l.id} className="cursor-pointer" onClick={() => toggleLease(l.id)}>
                      <TableCell><Checkbox checked={selectedLeases.has(l.id)} onCheckedChange={() => toggleLease(l.id)} /></TableCell>
                      <TableCell>{tenantName(l.tenants)}</TableCell>
                      <TableCell className="text-muted-foreground">{l.properties?.name || l.properties?.address || '—'}</TableCell>
                      <TableCell className="text-muted-foreground">{l.properties?.scis?.name || '—'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        </div>

        <DialogFooter className="items-center sm:justify-between gap-2">
          <span className="text-sm text-muted-foreground">
            {totalToCreate > 0 ? `${totalToCreate} quittance(s) à créer` : 'Aucune sélection'}
          </span>
          <Button onClick={() => generate.mutate()} disabled={generate.isPending || totalToCreate === 0}>
            {generate.isPending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Création…</> : 'Créer les quittances'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
